import { useEffect, useLayoutEffect, useRef, useState } from "react"
import type { MatterContext } from "../contract"
import { useDocsStore } from "../docs.store"
import { useTurnStore } from "../store"
import { retryTurn, sendTurn } from "./actions"
import { AnswerCard } from "./AnswerCard"
import { AnswerSections } from "./AnswerSections"
import { Glance } from "./Glance"
import { isAnatomy } from "./anatomy"
import { weigh, weighingLine } from "./derive"
import { Limits } from "../ds"
import { ReadingPlanBlock } from "./ReadingPlanBlock"
import { ClarifyCard } from "./ClarifyCard"
import { docWord } from "./docStatus"
import { ErrorCard } from "./ErrorCard"
import { contextParts, takeawayFor } from "./format"
import { useReducedMotion } from "./hooks"
import type { TimedStep } from "./Loader"
import { SimSuggestionCard } from "@/components/SimSuggestionCard"
import { SearchFold } from "./SearchFold"
import { useReaderContext } from "../readerContext"
import { useAnswerSession } from "./session"
import { Working } from "./Working"
import { WorkspaceLine } from "./WorkspaceEntry"

export const ASSUMED = "__assumed__"
const HOLD_MS = 1600

/** Loader hold: when a live turn finishes, the loader shows the beam at rest, then folds. */
function useDoneHold(status: string, live: boolean, reduced: boolean) {
  const prev = useRef(status)
  const [phase, setPhase] = useState<"none" | "hold" | "after">("none")
  useEffect(() => {
    const was = prev.current
    prev.current = status
    if (was !== "running" || status !== "done" || !live || reduced) return
    setPhase("hold")
    const a = window.setTimeout(() => setPhase("after"), HOLD_MS)
    const b = window.setTimeout(() => setPhase("none"), HOLD_MS * 2)
    return () => {
      window.clearTimeout(a)
      window.clearTimeout(b)
    }
  }, [status, live, reduced])
  return phase
}

/** True while `active` and the element's text is cut off (it overflows its box). Remeasures on resize. */
export function useTruncated(ref: React.RefObject<HTMLElement | null>, active: boolean, text: string): boolean {
  const [cut, setCut] = useState(false)
  useLayoutEffect(() => {
    const el = ref.current
    if (!active || !el) return
    const measure = () => setCut(el.scrollWidth > el.clientWidth)
    measure()
    if (typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref, active, text])
  return cut
}

/** One user turn: the question, then the working view, clarify card, verdict, sections in reading order, fold, error. */
export function Turn({ turnKey, threadId, number, inlineWorking = true, only, onGo }: {
  turnKey: string
  threadId: string
  number: number
  /** The working panel sits in the column; on wide screens the page puts it in the rail instead. */
  inlineWorking?: boolean
  /** One at a time: the slot to show. */
  only?: string | null
  onGo?: (id: string) => void
}) {
  const [showQuestion, setShowQuestion] = useState(false)
  const qRef = useRef<HTMLParagraphElement>(null)
  // Only the newest turn offers Edit; a re-run folds the turn it replaced.
  const isLatest = useTurnStore((s) => s.order[s.order.length - 1] === turnKey)
  const busy = useTurnStore((s) => s.order.some((k) => s.turns[k]?.status === "running"))
  const view = useTurnStore((s) => s.turns[turnKey])
  const reader = useReaderContext((s) => s.threads[threadId])
  const meta = useAnswerSession((s) => s.meta[turnKey])
  const stepT = useAnswerSession((s) => s.stepT[turnKey])
  const choices = useAnswerSession((s) => s.choices)
  const resolved = useAnswerSession((s) => s.resolved[turnKey])
  const docs = useDocsStore((s) => s.docs)
  const reduced = useReducedMotion()
  const phase = useDoneHold(view?.status ?? "", !!meta?.live, reduced)
  const folded = !!view && !!view.head && view.status !== "running" && !showQuestion
  const truncated = useTruncated(qRef, folded, view?.question ?? "")
  if (!view) return null

  const running = view.status === "running"
  const request = meta?.request ?? { message: view.question, mode: view.mode === "draft_check" ? "draft_check" : "ask", doc_ids: [] }
  const ask = (answers: Record<string, unknown>) => ({
    message: request.message || view.question,
    mode: request.mode,
    doc_ids: request.doc_ids,
    answers,
  })
  const s = useAnswerSession.getState()

  const onContext = (ctx: MatterContext) => {
    if (busy) return
    void sendTurn(threadId, ask({ ...(request.answers ?? {}), context: ctx }), "context", contextParts(ctx).join(" · "), turnKey)
  }
  const onAnswers = (answers: Record<string, string>) => {
    const summary = Object.values(answers).join(" · ")
    s.resolve(turnKey, summary)
    void sendTurn(threadId, ask(answers), "answers", summary)
  }
  const onAssume = () => {
    s.resolve(turnKey, ASSUMED)
    void sendTurn(threadId, ask({ assume: true }), "answers", ASSUMED)
  }

  const steps: TimedStep[] = view.steps.map((st) => ({ ...st, t: stepT?.[st.id] ?? 0 }))
  const plan = view.plan ?? []
  const takeaways = plan.map((p) => takeawayFor(p, view.sections[p.id]?.blocks, choices, turnKey))
  const anatomy = isAnatomy(plan)
  const progress = `${view.steps.length}:${view.steps.filter((x) => x.status !== "run").length}:${Object.keys(view.sections).length}:${view.head ? 1 : 0}`
  const answered = !running && !!view.head

  const showLoader = running || phase === "hold"
  const kind = meta?.kind ?? "ask"
  const superseded = !!meta?.supersededBy
  const attached = (request.doc_ids ?? []).filter((id) => docs[id])

  return (
    <section
      className={`turn${kind === "answers" ? " cont" : ""}`}
      id={`turn-${turnKey}`}
      data-turn={turnKey}
      aria-label={`Question ${number}`}
    >
      {kind === "ask" ? (
        <div className={`uq${answered && !showQuestion ? " folded" : ""}`}>
          <p ref={qRef}><span className="lab">You asked</span> <span className="uq-text">{view.question || request.message}</span></p>
          {answered && (truncated || showQuestion) ? (
            <button type="button" className="uq-toggle" aria-expanded={showQuestion} onClick={() => setShowQuestion((v) => !v)}>
              {showQuestion ? "Less" : "Full question"}
            </button>
          ) : null}
          {attached.length ? (
            <ul className="udocs" aria-label="Attached documents">
              {attached.map((id) => (
                <li className="udoc" key={id}>{docs[id].filename || "Document"} <span className="st">{docWord(docs[id].status)}</span></li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {superseded ? (
        <div className="rsum" data-superseded>Replaced by a newer answer below.</div>
      ) : null}
      {superseded ? null : <div className="stack ph-no-capture" data-answer-body>
        {showLoader && (inlineWorking || phase === "hold") ? (
          <Working
            id={`ld-${turnKey}`}
            steps={steps}
            mascot={phase === "hold" ? "done" : "searching"}
            matter={contextParts(view.head?.context).join(" · ")}
            progress={progress}
            after={phase === "hold" ? null : <WorkspaceLine threadId={threadId} />}
          />
        ) : null}
        {kind === "context" && meta?.summary ? (
          <div className="rsum">You told Nira: <span className="mono">{meta.summary}</span></div>
        ) : null}
        {view.clarify && resolved ? (
          <div className="rsum">
            {resolved === ASSUMED ? "You asked Nira to answer on stated assumptions." : <>You told Nira: <span className="mono">{resolved}</span></>}
          </div>
        ) : null}
        {view.clarify && !resolved && view.status === "awaiting_input" ? (
          <ClarifyCard id={`clar-${turnKey}`} clarify={view.clarify} onAnswers={onAnswers} onAssume={onAssume} />
        ) : null}
        {view.head ? (
          <AnswerCard id={`ans-${turnKey}`} head={view.head} onContext={onContext} weighing={weighingLine(weigh(view.sections))} reader={meta?.readerAt ?? reader} readOnly={!isLatest} busy={busy} />
        ) : null}
        {anatomy && !running ? <Glance sections={view.sections} /> : null}
        {anatomy && !running && inlineWorking ? (
          <ReadingPlanBlock turn={turnKey} plan={plan} sections={view.sections} limits={view.limits ?? []} onGo={onGo} />
        ) : null}
        {plan.length ? (
          <AnswerSections
            turn={turnKey}
            plan={plan}
            sections={view.sections}
            takeaways={takeaways}
            sources={view.sources}
            running={running}
            limits={view.limits ?? []}
            only={anatomy ? only : null}
            onGo={onGo}
            offers={{
              found: view.found ?? [],
              title: view.head?.title || view.question,
              department: kind === "ask" && reader?.side !== "revenue",
            }}
          />
        ) : null}
        {!anatomy && !running && view.limits?.length ? (
          <Limits title="What Nira cannot see" items={view.limits} className="alimits" />
        ) : null}
        {view.status === "done" && view.suggestedSimulator ? (
          <SimSuggestionCard simulator={view.suggestedSimulator} question={view.question || request.message} />
        ) : null}
        {!showLoader && view.status !== "awaiting_input" ? <SearchFold trail={view.trail} steps={steps} title="How Nira searched" /> : null}
        {view.error ? (
          <ErrorCard
            error={view.error}
            onRetry={meta && !meta.retried && !busy ? () => void retryTurn(threadId, turnKey) : undefined}
          />
        ) : null}
      </div>}
    </section>
  )
}
